/*
  Сверка скана: страницы, текущая страница, правка полей и решение по ней.

  Открывается на первой странице, которая ждёт человека. После решения
  переходит к следующей ждущей - так сверка идёт подряд, а не поиском.
*/

import { useCallback, useEffect, useState } from 'react'

import { api } from '../../api/client.js'
import { EMPTY_PERSON } from './scanFields.js'

function firstWaiting(pages, after = 0) {
  const next = pages.findIndex((page, index) => index >= after && page.state === 'waiting')
  if (next >= 0) return next
  const any = pages.findIndex((page) => page.state === 'waiting')
  return any >= 0 ? any : Math.min(after, Math.max(pages.length - 1, 0))
}

// Свой ключ у каждого добавленного человека: строку можно убрать из середины,
// и ключ по номеру перенёс бы введённое в соседние поля.
let lastKey = 0
const withKey = (person) => ({ ...person, key: `p${(lastKey += 1)}` })

// Ключ и отметки «сомнительно» - только для экрана, полями жильца они не являются.
const SCREEN_ONLY = new Set(['key', 'doubtful'])
const withoutKey = (person) => Object.fromEntries(Object.entries(person).filter(([name]) => !SCREEN_ONLY.has(name)))

/*
  Второй и следующие жильцы страницы - с 06.10.2026 их читает распознавание.
  Сомнительные поля сервер называет «others.0.passport»; на экране отметка
  держится за жильца, а не за номер: убранный из середины жилец не должен
  переносить жёлтое соседу.
*/
function peopleOf(page) {
  const doubtful = page?.doubtful ?? []
  return (page?.others ?? []).map((person, position) => {
    const prefix = `others.${position}.`
    const marked = doubtful.filter((name) => name.startsWith(prefix)).map((name) => name.slice(prefix.length))
    return { ...withKey(person), doubtful: marked }
  })
}

// Пустое поле и поле, которого в ответе сервера нет, - одно и то же: иначе
// страница считалась бы поправленной, едва человек наведёт фокус на графу.
function sameFields(left = {}, right = {}) {
  const names = new Set([...Object.keys(left), ...Object.keys(right)])
  return [...names].every((name) => (left[name] ?? '') === (right[name] ?? ''))
}

/*
  Есть ли на странице несохранённые правки (01.10.2026, проверка перед
  выкладкой): с этого дня большую часть граф человек вписывает руками, и
  «Назад», «Дальше», «Закрыть» или другой файл молча выбрасывали вписанное.
*/
function edited(page, values, others) {
  if (!page) return false
  const before = page.others ?? []
  return (
    !sameFields(values, page.values) ||
    others.length !== before.length ||
    others.some((person, position) => !sameFields(withoutKey(person), before[position]))
  )
}

export default function useScanReview(fileId, onDecided) {
  const [pages, setPages] = useState(null)
  const [index, setIndex] = useState(0)
  const [values, setValues] = useState({})
  const [others, setOthers] = useState([])
  const [work, setWork] = useState({ busy: false, error: null })

  const open = (page) => {
    setValues(page?.values ?? {})
    setOthers(peopleOf(page))
  }

  const load = useCallback(
    (after) =>
      api
        .scanPages(fileId)
        .then((found) => {
          const at = firstWaiting(found, after)
          setPages(found)
          setIndex(at)
          setValues(found[at]?.values ?? {})
          setOthers(peopleOf(found[at]))
        })
        .catch((error) => setWork({ busy: false, error })),
    [fileId],
  )

  useEffect(() => {
    load(0)
  }, [load])

  const goTo = (at) => {
    setIndex(at)
    open(pages[at])
    setWork({ busy: false, error: null })
  }
  const go = (step) => goTo(Math.min(Math.max(index + step, 0), pages.length - 1))
  // К странице по её номеру - из итога «Сверить все» (19.10.2026).
  const show = (number) => {
    const at = pages.findIndex((item) => item.number === number)
    if (at >= 0) goTo(at)
  }
  // Страницы заново - после «Сверить все»: открывается первая оставшаяся.
  const reload = () => load(0)

  const decide = async (confirm) => {
    setWork({ busy: true, error: null })
    // Ключ и отметки - только для экрана, на сервер уходят поля человека.
    const people = others.map(withoutKey)
    try {
      await api.decideScanPage(fileId, pages[index].number, {
        confirm,
        values: confirm ? values : {},
        others: confirm ? people : [],
      })
      setWork({ busy: false, error: null })
      await load(index + 1)
      onDecided?.()
    } catch (error) {
      setWork({ busy: false, error })
    }
  }

  const edit = (name, value) => setValues((current) => ({ ...current, [name]: value }))

  const people = {
    list: others,
    add: () => setOthers((current) => [...current, withKey(EMPTY_PERSON)]),
    remove: (key) => setOthers((current) => current.filter((person) => person.key !== key)),
    edit: (key, name, value) =>
      setOthers((current) =>
        current.map((person) => (person.key === key ? { ...person, [name]: value } : person)),
      ),
  }

  const page = pages?.[index]
  return {
    pages,
    index,
    page,
    values,
    people,
    work,
    go,
    show,
    reload,
    decide,
    edit,
    dirty: edited(page, values, others),
  }
}
